/**
 * Mobile bottom toolbar with 6 primary mode icons + undo/redo
 * Tapping a mode icon opens the panel sheet directly to that mode.
 */

import { cn } from '@frameit/ui';
import { useProjectStore, type EditorMode } from '../../stores/projectStore';

/** Mode icons for the bottom bar */
const TOOLBAR_MODES: { id: EditorMode; label: string; icon: string }[] = [
  {
    id: 'frame',
    label: 'Frame',
    icon: 'M4 3h16a1 1 0 011 1v16a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1zm1 2v14h14V5H5z',
  },
  { id: 'mat', label: 'Mat', icon: 'M4 4h16v16H4V4zm2 2v12h12V6H6z' },
  {
    id: 'image',
    label: 'Image',
    icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  },
  {
    id: 'scene',
    label: 'Scene',
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  },
  {
    id: 'effects',
    label: 'Effects',
    icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z',
  },
  {
    id: 'gallery',
    label: 'Gallery',
    icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z',
  },
  {
    id: 'export',
    label: 'Export',
    icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4',
  },
];

export function MobileToolbar(): React.JSX.Element {
  const editorMode = useProjectStore((s) => s.editorMode);
  const setEditorMode = useProjectStore((s) => s.setEditorMode);
  const setMobilePanelOpen = useProjectStore((s) => s.setMobilePanelOpen);
  const undo = useProjectStore((s) => s.undo);
  const redo = useProjectStore((s) => s.redo);
  const canUndo = useProjectStore((s) => s.canUndo);
  const canRedo = useProjectStore((s) => s.canRedo);

  const handleModeClick = (mode: EditorMode): void => {
    setEditorMode(mode);
    setMobilePanelOpen(true);
  };

  return (
    <div className="border-t bg-background pb-safe-bottom">
      {/* Compact undo/redo row */}
      <div className="flex items-center justify-between px-3 h-8 border-b">
        <div className="flex gap-2">
          <button
            onClick={undo}
            disabled={!canUndo()}
            className="flex items-center justify-center w-10 h-8 text-muted-foreground disabled:opacity-30"
            aria-label="Undo"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h10a4 4 0 014 4v2M3 10l4-4m-4 4l4 4"
              />
            </svg>
          </button>
          <button
            onClick={redo}
            disabled={!canRedo()}
            className="flex items-center justify-center w-10 h-8 text-muted-foreground disabled:opacity-30"
            aria-label="Redo"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 10H11a4 4 0 00-4 4v2m14-6l-4-4m4 4l-4 4"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* 6-icon mode bar */}
      <div className="flex items-center justify-around px-1 h-14">
        {TOOLBAR_MODES.map((mode) => (
          <button
            key={mode.id}
            onClick={() => handleModeClick(mode.id)}
            className={cn(
              'flex flex-col items-center justify-center gap-0.5 w-14 h-12 rounded-lg',
              'transition-colors active:scale-95',
              editorMode === mode.id ? 'text-primary' : 'text-muted-foreground',
            )}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={mode.icon} />
            </svg>
            <span className="text-[10px] leading-none font-medium">{mode.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
